import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { NODE_API_BASE_URL } from "./config";
import { DatePicker, Tooltip } from "antd";
import dayjs from "dayjs";
import { authHeaders } from "./cms/utils/session";
import DashboardCharts from "./dashboard/DashboardCharts";
import RevenueReports from "./dashboard/RevenueReports";

const { RangePicker } = DatePicker;

/**
 * Which of the two readings of this data is on screen.
 *
 * Remembered per browser because it is a reading preference, not state anyone
 * else needs: someone who thinks in charts should not have to switch every time
 * they open the dashboard. Wrapped because storage throws in a private window.
 */
const VIEW_KEY = "cms_dashboard_view";
const readView = () => {
  try {
    return localStorage.getItem(VIEW_KEY) === "charts" ? "charts" : "cards";
  } catch {
    return "cards";
  }
};

// ── helpers ───────────────────────────────────────────────────────────────────
const fmt = (n) => n ? `₹${Number(n).toLocaleString("en-IN")}` : "₹0";
const planColor = (name = "") => {
  const n = name.toLowerCase();
  if (n.includes("diamond")) return { bar: "linear-gradient(90deg,#6366f1,#818cf8)", dot: "#6366f1" };
  if (n.includes("gold"))    return { bar: "#f59e0b", dot: "#f59e0b" };
  if (n.includes("silver"))  return { bar: "#94a3b8", dot: "#94a3b8" };
  return { bar: "#e87a4e", dot: "#e87a4e" };
};
const typeLabel = (t = "") => {
  const v = (t || "").toLowerCase();
  if (v.includes("annual") || v === "annualy") return { label: "Annually",    color: "#6366f1", bg: "rgba(99,102,241,0.1)" };
  if (v.includes("month"))                     return { label: "Monthly",    color: "#06b6d4", bg: "rgba(6,182,212,0.1)" };
  if (v.includes("free") || v.includes("trial")) return { label: "Free Trial", color: "#f59e0b", bg: "rgba(245,158,11,0.1)" };
  return { label: t || "—", color: "#6b7280", bg: "rgba(107,114,128,0.1)" };
};
const fmtDate = (s) => {
  if (!s) return "—";
  if (s.includes("/")) return s;
  const d = new Date(s);
  return isNaN(d) ? s : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// ── shimmer ───────────────────────────────────────────────────────────────────
const Shimmer = ({ w = "100%", h = 16, r = 6 }) => (
  <div style={{ width: w, height: h, borderRadius: r, background: "linear-gradient(90deg,#f0f4f8 25%,#e8edf2 50%,#f0f4f8 75%)", backgroundSize: "200% 100%", animation: "dbShimmer 1.4s infinite" }} />
);

// ── donut chart ───────────────────────────────────────────────────────────────
function DonutChart({ plans }) {
  const total = plans.reduce((s, p) => s + p.count, 0);
  const colors = ["#6366f1", "#f59e0b", "#94a3b8", "#e87a4e"];
  const C = 2 * Math.PI * 35;
  let offset = 0;
  const slices = plans.map((p, i) => {
    const dash = total ? (p.count / total) * C : 0;
    const s = { dash, offset, color: colors[i] };
    offset += dash;
    return s;
  });
  return (
    <svg width="88" height="88" viewBox="0 0 90 90" style={{ flexShrink: 0 }}>
      <circle cx="45" cy="45" r="35" fill="none" stroke="#f1f5f9" strokeWidth="11" />
      {slices.map((s, i) => (
        <circle key={i} cx="45" cy="45" r="35" fill="none"
          stroke={s.color} strokeWidth="11" strokeLinecap="round"
          strokeDasharray={`${s.dash} ${C}`} strokeDashoffset={C / 4 - s.offset}
        />
      ))}
      <text x="45" y="49" textAnchor="middle" fill="#374151" fontSize="11" fontFamily="sans-serif" fontWeight="700">Plans</text>
    </svg>
  );
}

// ── shared style objects ──────────────────────────────────────────────────────
const card  = { background: "#fff", borderRadius: 14, border: "1px solid rgba(0,0,0,0.07)", boxShadow: "0 1px 4px rgba(0,0,0,0.05),0 4px 18px rgba(0,0,0,0.04)", padding: 20 };
const h2    = { fontSize: 14, fontWeight: 700, color: "#111827", marginBottom: 16 };
const thSt  = { textAlign: "left", padding: "8px 12px", color: "#9ca3af", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.4px", borderBottom: "1px solid #f1f5f9", background: "#fafbff", position: "sticky", top: 0, zIndex: 1 };
const tdSt  = { padding: "9px 12px", borderBottom: "1px solid #f8faff" };

// ─────────────────────────────────────────────────────────────────────────────
export default function Dashboard() {
  const navigate = useNavigate();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [dates,   setDates]   = useState(null);
  const [view,    setView]    = useState(readView);

  const chooseView = (v) => {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* not worth failing over */ }
  };

  const fetchData = useCallback(async (from, to) => {
    setLoading(true);
    try {
      const params = {};
      if (from && to) { params.from_date = from.format("YYYY-MM-DD"); params.to_date = to.format("YYYY-MM-DD"); }
      const res = await axios.get(`${NODE_API_BASE_URL}/api/dashboard`, {
        params,
        headers: { ...authHeaders() },
      });
      setData(res.data?.data ?? null);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(null, null); }, []); // eslint-disable-line

  const kpi       = data?.kpi || {};
  const rev       = data?.revenue || {};
  const plans     = data?.plan_distribution || [];
  const subs      = data?.subscription_types || {};
  const pmodes    = data?.payment_modes || [];
  const seg       = data?.customer_segments || {};
  const txns      = data?.recent_transactions || [];
  const freeList  = data?.free_trial_user_list || [];
  const paidList  = data?.paid_user_list || [];
  const enquiries = data?.recent_enquiry_list || [];
  const tm        = data?.trademark_monitoring || {};

  /* last 7 days, today included — the rule the dashboard KPI uses. */
  const newThisWeekWindow = {
    subscription_created_from: dayjs().subtract(6, "day").format("YYYY-MM-DD"),
    subscription_created_to: dayjs().format("YYYY-MM-DD"),
  };
  const tmTotal    = Number(tm.total_trademarks || 0);
  const tmDistinct = Number(tm.total_distinct_trademarks || 0);
  const tmMonitor  = Number(tm.total_monitoring || 0);
  const pctOf = (part, whole) => (whole > 0 ? Math.round((part / whole) * 100) : 0);
  const tmDistinctPct  = pctOf(tmDistinct, tmTotal);
  const tmMonitoredPct = pctOf(tmMonitor, tmTotal);
  const barMax    = Math.max(...(rev.monthly_breakdown || []).map((m) => m.amount), 1);

  const css = `
    @keyframes dbShimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
    @keyframes dbFadeUp  { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
    .db-kpi { animation:dbFadeUp .35s ease both; transition:box-shadow .2s,transform .2s; cursor:default; }
    .db-kpi:hover { box-shadow:0 6px 24px rgba(0,0,0,0.1)!important; transform:translateY(-2px); }
    .db-bar:hover { opacity:.65; }
    .db-tr:hover td { background:rgba(232,122,78,0.03)!important; }
    .db-sc::-webkit-scrollbar{width:4px}
    .db-sc::-webkit-scrollbar-track{background:transparent}
    .db-sc::-webkit-scrollbar-thumb{background:#e2e8f0;border-radius:99px}
  `;

  return (
    <div className="main-content">
      <div className="page-content">
        <div className="container-fluid">
          <style>{css}</style>
          <div style={{ background: "#f4f7ff", margin: "-24px -24px 0", padding: "26px 28px", minHeight: "100vh", fontFamily: "-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif" }}>

            {/* ── Header ── */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 22 }}>
              <div>
                <div style={{ fontSize: 21, fontWeight: 700, color: "#111827" }}>Dashboard</div>
                <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 2 }}>Welcome back — here's what's happening</div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {/* One switch for the whole screen. The numbers are identical
                    either way — only the form changes, so it sits with the date
                    range rather than being repeated on every card. */}
                <div style={{ display: "inline-flex", background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, padding: 2 }}>
                  {[
                    { key: "cards", label: "Cards" },
                    { key: "charts", label: "Charts" },
                  ].map((v) => (
                    <button
                      key={v.key}
                      onClick={() => chooseView(v.key)}
                      aria-pressed={view === v.key}
                      style={{
                        padding: "5px 15px", borderRadius: 7, border: "none", cursor: "pointer",
                        fontSize: 12.5, fontWeight: 600,
                        background: view === v.key ? "#eef2ff" : "transparent",
                        color: view === v.key ? "#4f46e5" : "#6b7280",
                      }}
                    >
                      {v.label}
                    </button>
                  ))}
                </div>
                <RangePicker
                  value={dates}
                  onChange={(d) => { setDates(d || null); fetchData(d ? d[0] : null, d ? d[1] : null); }}
                  size="small" style={{ borderRadius: 8 }}
                />
                {dates && (
                  <button onClick={() => { setDates(null); fetchData(null, null); }}
                    style={{ background: "#f1f5f9", color: "#6b7280", border: "1px solid #e2e8f0", padding: "6px 14px", borderRadius: 8, fontSize: 13, cursor: "pointer", fontWeight: 500 }}>
                    Reset
                  </button>
                )}
                <button onClick={() => fetchData(dates ? dates[0] : null, dates ? dates[1] : null)}
                  style={{ background: "#e87a4e", color: "#fff", border: "none", padding: "7px 16px", borderRadius: 8, fontSize: 13, cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                  ↻ Refresh
                </button>
              </div>
            </div>

            {/* ── KPI Cards: 5×2 grid ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 14, marginBottom: 20 }}>
              {[
                { label: "Total Revenue",        val: fmt(rev.total_collected),              icon: "💰", accent: "#059669", delta: "All time collected",          link: "/cms/payment-list" },
                { label: "Total Customers",      val: kpi.total_customers,                   icon: "👥", accent: "#6366f1", delta: `+${kpi.new_customers_this_week || 0} this week`, link: "/cms/manage-customer" },
                { label: "Active Subscriptions", val: kpi.active_paid_subscriptions,         icon: "✅", accent: "#10b981", delta: "Paid & active",               link: "/cms/manage-customer/active-paid" },
                { label: "Paid Expired",         val: kpi.paid_expired_count,                icon: "❌", accent: "#dc2626", delta: "Subscription ended",          link: "/cms/manage-customer/paid-expired" },
                { label: "Active Free Trial",    val: kpi.total_free_trial_users,            icon: "🔖", accent: "#f59e0b", delta: "Active trials",               link: "/cms/manage-customer/active-free-trial" },
                { label: "Free Trial Expired",   val: kpi.free_trial_expired_count,          icon: "⏰", accent: "#7c3aed", delta: "Trial period ended",          link: "/cms/manage-customer/free-trial-expired" },
                { label: "Pending Payment",      val: kpi.pending_payments_count,            icon: "⏳", accent: "#d97706", delta: "Awaiting payment",            link: "/cms/manage-customer/pending-payment" },
                { label: "Expiring Soon",        val: kpi.expiring_soon_count,               icon: "⚠️", accent: "#ea580c", delta: "Within 7 days",              link: "/cms/manage-customer/expiring-soon" },
                /* Same window the KPI counts: last 7 days including today, by the
                   customer's FIRST order. Sending it along means the card's number
                   and the list's count can never disagree. */
                { label: "New This Week",        val: kpi.new_customers_this_week,           icon: "🆕", accent: "#0891b2", delta: "New customers",              link: "/cms/manage-customer", linkState: newThisWeekWindow },
                { label: "Total Enquiries",      val: kpi.total_enquiries,                   icon: "📩", accent: "#06b6d4", delta: "All time",                   link: "/cms/manageenquiry" },
              ].map((k, i) => (
                <div key={i} className="db-kpi"
                  onClick={() => k.link && navigate(k.link, k.linkState ? { state: k.linkState } : {})}
                  style={{ ...card, padding: "18px 16px", borderTop: `3px solid ${k.accent}`, animationDelay: `${i * 0.05}s`, cursor: k.link ? "pointer" : "default" }}>
                  <div style={{ fontSize: 22, marginBottom: 10 }}>{k.icon}</div>
                  {loading
                    ? <Shimmer h={26} w="70%" />
                    : <div style={{ fontSize: 22, fontWeight: 700, color: "#111827", lineHeight: 1 }}>{k.val ?? "—"}</div>}
                  <div style={{ fontSize: 12, color: "#6b7280", marginTop: 5, fontWeight: 500 }}>{k.label}</div>
                  <div style={{ fontSize: 11, color: k.accent, marginTop: 5, fontWeight: 500 }}>{k.delta}</div>
                </div>
              ))}
            </div>

            {/* The same numbers, two readings.

                The KPI row above stays in both: a headline figure is already
                in its best form as a stat tile, and turning ten of them into
                a bar chart would bury each one. What switches is everything
                below, where the questions are shares and trends. */}
            {view === "cards" ? (
              <>
              {/* ── Trademark monitoring ── */}
              {/* ── Trademark monitoring — same white-card language as the KPI
                     row above, just with a share bar so the three numbers read
                     against each other. ── */}
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12, flexWrap: "wrap", gap: 8 }}>
                <div style={h2 && { ...h2, marginBottom: 0 }}>Trademark Monitoring(Customers)</div>
             
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 14, marginBottom: 20 }}>
                {[
                  // Display only — these cards do not navigate anywhere.
                  {
                    label: "Total Trademarks", val: tm.total_trademarks, icon: "™",
                    accent: "#2563eb", note: "All records in the registry", share: null,
                  },
                  {
                    label: "Total Distinct Trademarks", val: tm.total_distinct_trademarks, icon: "◈",
                    accent: "#7c3aed", note: `${tmDistinctPct}% of total records`, share: tmDistinctPct,
                  },
                  {
                    label: "Total Monitoring", val: tm.total_monitoring, icon: "◎",
                    accent: "#059669", note: "Paid + Free Trial", share: tmMonitoredPct,
                  },
                ].map((k, i) => (
                  <div key={i} className="db-kpi"
                    style={{ ...card, padding: "18px 18px", borderTop: `3px solid ${k.accent}`, animationDelay: `${i * 0.05}s`, cursor: "default" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                      <div style={{
                        width: 30, height: 30, borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center",
                        background: `${k.accent}14`, border: `1px solid ${k.accent}33`, color: k.accent, fontSize: 15, fontWeight: 700,
                      }}>
                        {k.icon}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>{k.label}</div>
                    </div>

                    {loading
                      ? <Shimmer h={28} w="60%" />
                      : <div style={{ fontSize: 26, fontWeight: 700, color: "#111827", lineHeight: 1, letterSpacing: -0.4, fontVariantNumeric: "tabular-nums" }}>
                          {k.val != null ? Number(k.val).toLocaleString("en-IN") : "—"}
                        </div>}

                    {k.share != null && (
                      <div style={{ height: 4, borderRadius: 99, background: "#f1f5f9", marginTop: 12, overflow: "hidden" }}>
                        <div style={{ width: `${loading ? 0 : Math.min(100, k.share)}%`, height: "100%", borderRadius: 99, background: k.accent, transition: "width .6s ease" }} />
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "#9ca3af", marginTop: k.share != null ? 8 : 12 }}>{k.note}</div>
                  </div>
                ))}
              </div>

              {/* ── Revenue ── */}
              <div style={{ ...card, marginBottom: 20 }}>
                <div style={h2}>Monthly Revenue</div>
                <div style={{ display: "flex", background: "#f8faff", borderRadius: 10, border: "1px solid rgba(0,0,0,0.05)", marginBottom: 18 }}>
                  {loading
                    ? Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} style={{ flex: 1, padding: "12px 8px", borderRight: i < 5 ? "1px solid rgba(0,0,0,0.05)" : "none" }}>
                        <Shimmer h={20} w="60%" /><div style={{ marginTop: 4 }}><Shimmer h={11} w="80%" /></div>
                      </div>
                    ))
                    : (rev.monthly_breakdown || []).map((m, i, arr) => {
                      const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                      const monthIdx = monthNames.indexOf(m.month);
                      const year = Number(m.year);
                      const fromDate = monthIdx >= 0 ? `${year}-${String(monthIdx + 1).padStart(2, "0")}-01` : "";
                      const lastDay = monthIdx >= 0 ? new Date(year, monthIdx + 1, 0).getDate() : "";
                      const toDate = monthIdx >= 0 ? `${year}-${String(monthIdx + 1).padStart(2, "0")}-${lastDay}` : "";
                      return (
                        <div key={i} onClick={() => fromDate && navigate("/cms/payment-list", { state: { fromDate, toDate } })}
                          style={{ flex: 1, textAlign: "center", padding: "12px 8px", borderRight: i < arr.length - 1 ? "1px solid rgba(0,0,0,0.05)" : "none", cursor: "pointer" }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "rgba(99,102,241,0.05)"}
                          onMouseLeave={(e) => e.currentTarget.style.background = ""}>
                          <div style={{ fontSize: 18, fontWeight: 700, color: i === arr.length - 1 ? "#e87a4e" : i === arr.length - 2 ? "#6366f1" : "#94a3b8" }}>{fmt(m.amount)}</div>
                          <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 3 }}>{m.month} {m.year}</div>
                        </div>
                      );
                    })}
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: 8, height: 110 }}>
                  {loading
                    ? Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, height: "100%", justifyContent: "flex-end" }}>
                        <div style={{ width: "100%", height: `${20 + i * 12}%`, borderRadius: "5px 5px 0 0", background: "#f0f4f8" }} />
                        <Shimmer h={10} w="80%" r={4} />
                      </div>
                    ))
                    : (rev.monthly_breakdown || []).map((m, i, arr) => {
                      const bh = Math.max(6, (m.amount / barMax) * 100);
                      const isLast = i === arr.length - 1;
                      const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                      const monthIdx = monthNames.indexOf(m.month);
                      const year = Number(m.year);
                      const fromDate = monthIdx >= 0 ? `${year}-${String(monthIdx + 1).padStart(2, "0")}-01` : "";
                      const lastDay = monthIdx >= 0 ? new Date(year, monthIdx + 1, 0).getDate() : "";
                      const toDate = monthIdx >= 0 ? `${year}-${String(monthIdx + 1).padStart(2, "0")}-${lastDay}` : "";
                      return (
                        <div key={i} className="db-bar" onClick={() => fromDate && navigate("/cms/payment-list", { state: { fromDate, toDate } })}
                          style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4, height: "100%", justifyContent: "flex-end", cursor: "pointer" }}>
                          <div style={{ width: "100%", height: `${bh}%`, borderRadius: "5px 5px 0 0", background: isLast ? "linear-gradient(180deg,#e87a4e,#f59e0b)" : `rgba(99,102,241,${0.15 + (i / arr.length) * 0.55})` }} />
                          <div style={{ fontSize: 10, color: isLast ? "#e87a4e" : "#9ca3af", fontWeight: isLast ? 700 : 400 }}>{m.month}</div>
                        </div>
                      );
                    })}
                </div>
              </div>

              {/* ── Plan Distribution + Subscription Types + Customer Segments ── */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 20 }}>

                <div style={card}>
                  <div style={h2}>Plan Distribution</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 18 }}>
                    {loading ? <Shimmer w={88} h={88} r={50} /> : <DonutChart plans={plans} />}
                    <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                      {loading
                        ? Array.from({ length: 3 }).map((_, i) => <Shimmer key={i} h={13} w={110} />)
                        : plans.map((p, i) => {
                          const c = planColor(p.plan_name);
                          return (
                            <div key={i} onClick={() => navigate("/cms/manage-customer", { state: { plan_name: p.plan_name } })} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12.5, cursor: "pointer" }}>
                              <div style={{ width: 10, height: 10, borderRadius: 3, background: c.dot, flexShrink: 0 }} />
                              <span style={{ color: "#6b7280" }}>{p.plan_name.replace(" Plan", "")}</span>
                              <span style={{ marginLeft: "auto", fontWeight: 700, color: "#111827", paddingLeft: 12 }}>{p.percentage}%</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {loading
                      ? Array.from({ length: 3 }).map((_, i) => <Shimmer key={i} h={13} />)
                      : plans.map((p, i) => {
                        const c = planColor(p.plan_name);
                        return (
                          <div key={i} onClick={() => navigate("/cms/manage-customer", { state: { plan_name: p.plan_name } })} style={{ cursor: "pointer" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12.5, marginBottom: 5 }}>
                              <span style={{ color: "#6b7280" }}>{p.plan_name.replace(" Plan", "")}</span>
                              <span style={{ fontWeight: 600, color: "#111827" }}>{p.count}</span>
                            </div>
                            <div style={{ height: 6, borderRadius: 99, background: "#f1f5f9", overflow: "hidden" }}>
                              <div style={{ height: "100%", borderRadius: 99, width: `${p.percentage}%`, background: c.bar }} />
                            </div>
                          </div>
                        );
                      })}
                  </div>
                </div>

                <div style={card}>
                  <div style={h2}>Plan Types</div>
                  <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
                    {[
                      { label: "Annually",   val: subs.annual,     color: "#6366f1", bg: "rgba(99,102,241,0.08)",  border: "rgba(99,102,241,0.2)",  planType: "annually" },
                      { label: "Monthly",    val: subs.monthly,    color: "#06b6d4", bg: "rgba(6,182,212,0.08)",   border: "rgba(6,182,212,0.2)",   planType: "monthly" },
                      { label: "Free Trial", val: subs.free_trial, color: "#f59e0b", bg: "rgba(245,158,11,0.08)",  border: "rgba(245,158,11,0.2)",  planType: "free_trial" },
                    ].map((s, i) => (
                      <div key={i}
                        onClick={() => navigate("/cms/manage-customer", { state: { plan_type: s.planType } })}
                        style={{ flex: 1, background: s.bg, border: `1px solid ${s.border}`, borderRadius: 10, padding: 14, textAlign: "center", cursor: "pointer" }}>
                        {loading ? <Shimmer h={22} w="60%" /> : <div style={{ fontSize: 22, fontWeight: 700, color: s.color }}>{s.val ?? 0}</div>}
                        <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 3 }}>{s.label}</div>
                      </div>
                    ))}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: "#374151", marginBottom: 12 }}>Payment Modes</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {loading
                      ? Array.from({ length: 4 }).map((_, i) => <Shimmer key={i} h={15} />)
                      : pmodes.map((pm, i) => {
                        const maxC   = Math.max(...pmodes.map((p) => p.count), 1);
                        const icons  = { upi: "📱", cash: "💵", bank_transfer: "🏦", internal: "🧾" };
                        const labels = { upi: "UPI", cash: "Cash", bank_transfer: "Bank Transfer", internal: "Internal" };
                        const bC     = ["#6366f1", "#e87a4e", "#06b6d4", "#10b981"];
                        return (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5 }}>
                            <span style={{ fontSize: 16, width: 28, textAlign: "center" }}>{icons[pm.mode] || "💳"}</span>
                            <span style={{ flex: 1, color: "#6b7280" }}>{labels[pm.mode] || pm.mode}</span>
                            <div style={{ width: 80, height: 5, background: "#f1f5f9", borderRadius: 99, overflow: "hidden" }}>
                              <div style={{ height: "100%", borderRadius: 99, width: `${Math.max(5, (pm.count / maxC) * 100)}%`, background: bC[i] }} />
                            </div>
                            <span style={{ fontWeight: 600, color: "#374151", minWidth: 20, textAlign: "right" }}>{pm.count}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>

                <div style={card}>
                  <div style={h2}>Customer Segments</div>
                  <div style={{ display: "flex", gap: 10, marginBottom: 18 }}>
                    <div onClick={() => navigate("/cms/manage-customer", { state: { is_attorney: 1 } })}
                      style={{ flex: 1, background: "rgba(99,102,241,0.07)", border: "1px solid rgba(99,102,241,0.18)", borderRadius: 10, padding: 14, textAlign: "center", cursor: "pointer" }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"} onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}>
                      {loading ? <Shimmer h={22} w="60%" /> : <div style={{ fontSize: 22, fontWeight: 700, color: "#6366f1" }}>{seg.attorneys ?? 0}</div>}
                      <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 3 }}>⚖️ Attorneys</div>
                    </div>
                    <div onClick={() => navigate("/cms/manage-customer", { state: { is_attorney: 0 } })}
                      style={{ flex: 1, background: "rgba(232,122,78,0.07)", border: "1px solid rgba(232,122,78,0.18)", borderRadius: 10, padding: 14, textAlign: "center", cursor: "pointer" }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = "0.8"} onMouseLeave={(e) => e.currentTarget.style.opacity = "1"}>
                      {loading ? <Shimmer h={22} w="60%" /> : <div style={{ fontSize: 22, fontWeight: 700, color: "#e87a4e" }}>{seg.corporates ?? 0}</div>}
                      <div style={{ fontSize: 11.5, color: "#6b7280", marginTop: 3 }}>🏢 Corporates</div>
                    </div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 9 }}>GST Registered</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                    <div style={{ flex: 1, height: 7, background: "#f1f5f9", borderRadius: 99, overflow: "hidden" }}>
                      <div style={{ width: `${seg.gst_registered_percentage || 0}%`, height: "100%", background: "linear-gradient(90deg,#10b981,#6ee7b7)", borderRadius: 99 }} />
                    </div>
                    <div style={{ fontSize: 12, color: "#9ca3af", whiteSpace: "nowrap" }}>{seg.gst_registered_percentage ?? 0}% with GST</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: "#374151", marginBottom: 10 }}>Top States</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
                    {loading
                      ? Array.from({ length: 5 }).map((_, i) => <Shimmer key={i} h={13} />)
                      : (seg.top_states || []).map((s, i) => (
                        <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#6b7280" }}>
                          <span style={{ textTransform: "capitalize" }}>{s.state}</span>
                          <span style={{ fontWeight: 600, color: "#374151" }}>{s.count}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
              </>
            ) : (
              <DashboardCharts data={loading ? null : data} />
            )}

            {/* Revenue reporting — its own endpoint, its own period control,
                and it renders itself in whichever view is selected. */}
            <RevenueReports view={view} dates={dates} />

            {/* ── Recent Transactions ── */}
            <div style={{ ...card, marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 16 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Recent Transactions</span>
                <button onClick={() => navigate("/cms/payment-list")}
                  style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)", color: "#10b981", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  View All →
                </button>
              </div>
              <div className="db-sc" style={{ maxHeight: 270, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                  <thead>
                    <tr>{["Invoice ID", "Customer", "Plan", "Type", "Amount", "Date", "Status"].map((h) => <th key={h} style={thSt}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {loading
                      ? Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>{[120, 140, 80, 70, 70, 80, 60].map((w, j) => <td key={j} style={{ padding: "10px 12px" }}><Shimmer h={13} w={w} /></td>)}</tr>
                      ))
                      : txns.map((t, i) => {
                        const isPaid = t.payment_status === "1" || t.payment_status === 1;
                        const ti = typeLabel(t.plan_type);
                        return (
                          <tr key={i} className="db-tr">
                            <td style={{ ...tdSt, fontFamily: "monospace", color: "#6366f1", fontSize: 11.5 }}>{t.invoice_id}</td>
                            <td style={{ ...tdSt, color: "#111827", fontWeight: 500, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{t.customer_name}</td>
                            <td style={{ ...tdSt, color: "#6b7280" }}>{(t.plan_name || "").replace(" Plan", "")}</td>
                            <td style={tdSt}><span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 500, background: ti.bg, color: ti.color }}>{ti.label}</span></td>
                            <td style={{ ...tdSt, color: "#10b981", fontWeight: 600 }}>₹{Number(t.paid_amount || 0).toLocaleString("en-IN")}</td>
                            <td style={{ ...tdSt, color: "#9ca3af" }}>{t.payment_date}</td>
                            <td style={tdSt}><span style={{ display: "inline-flex", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 500, background: isPaid ? "rgba(16,185,129,0.1)" : "rgba(245,158,11,0.1)", color: isPaid ? "#10b981" : "#f59e0b" }}>{isPaid ? "Paid" : "Pending"}</span></td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Free Trial Users + Paid Users ── */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 20 }}>

              <div style={card}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 99, background: "#f59e0b", flexShrink: 0 }} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Free Trial Users</span>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>latest 10</span>
                  <button onClick={() => navigate("/cms/manage-customer", { state: { subscription_type: "free_trial" } })}
                    style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, background: "rgba(245,158,11,0.08)", border: "1px solid rgba(245,158,11,0.25)", color: "#f59e0b", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    View All →
                  </button>
                </div>
                <div className="db-sc" style={{ maxHeight: 300, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr>{["Name", "Email", "Mobile", "Trial Ends"].map((h) => <th key={h} style={thSt}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {loading
                        ? Array.from({ length: 5 }).map((_, i) => (
                          <tr key={i}>{[110, 140, 90, 90].map((w, j) => <td key={j} style={{ padding: "9px 12px" }}><Shimmer h={12} w={w} /></td>)}</tr>
                        ))
                        : freeList.map((u, i) => (
                          <tr key={i} className="db-tr">
                            <td style={{ ...tdSt, color: "#111827", fontWeight: 500, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</td>
                            <td style={{ ...tdSt, color: "#6b7280", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</td>
                            <td style={{ ...tdSt, color: "#6b7280", whiteSpace: "nowrap" }}>{u.mobile}</td>
                            <td style={tdSt}><span style={{ display: "inline-flex", padding: "2px 7px", borderRadius: 20, fontSize: 11, fontWeight: 500, background: "rgba(245,158,11,0.1)", color: "#f59e0b", whiteSpace: "nowrap" }}>{fmtDate(u.subscription_end_date)}</span></td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </div>

              <div style={card}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                  <div style={{ width: 8, height: 8, borderRadius: 99, background: "#10b981", flexShrink: 0 }} />
                  <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Paid Users</span>
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>latest 10</span>
                  <button onClick={() => navigate("/cms/manage-customer", { state: { subscription_type: "paid" } })}
                    style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, background: "rgba(16,185,129,0.08)", border: "1px solid rgba(16,185,129,0.25)", color: "#10b981", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                    View All →
                  </button>
                </div>
                <div className="db-sc" style={{ maxHeight: 300, overflowY: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr>{["Name", "Email", "Type", "Expires"].map((h) => <th key={h} style={thSt}>{h}</th>)}</tr>
                    </thead>
                    <tbody>
                      {loading
                        ? Array.from({ length: 5 }).map((_, i) => (
                          <tr key={i}>{[130, 150, 70, 90].map((w, j) => <td key={j} style={{ padding: "9px 12px" }}><Shimmer h={12} w={w} /></td>)}</tr>
                        ))
                        : paidList.map((u, i) => {
                          const ti = typeLabel(u.subscription_type || u.plan_type);
                          return (
                            <tr key={i} className="db-tr">
                              <td style={{ ...tdSt, color: "#111827", fontWeight: 500, maxWidth: 130, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.name}</td>
                              <td style={{ ...tdSt, color: "#6b7280", maxWidth: 150, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</td>
                              <td style={tdSt}><span style={{ display: "inline-flex", padding: "2px 7px", borderRadius: 20, fontSize: 11, fontWeight: 500, background: ti.bg, color: ti.color }}>{ti.label}</span></td>
                              <td style={{ ...tdSt, color: "#9ca3af", fontSize: 11, whiteSpace: "nowrap" }}>{fmtDate(u.subscription_end_date)}</td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* ── Recent Enquiries ── */}
            <div style={{ ...card, marginBottom: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <div style={{ width: 8, height: 8, borderRadius: 99, background: "#6366f1", flexShrink: 0 }} />
                <span style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>Recent Enquiries</span>
                <span style={{ fontSize: 11, color: "#9ca3af" }}>latest 10</span>
                <button onClick={() => navigate("/cms/manageenquiry")}
                  style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4, background: "rgba(99,102,241,0.08)", border: "1px solid rgba(99,102,241,0.25)", color: "#6366f1", borderRadius: 6, padding: "3px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
                  View All →
                </button>
              </div>
              <div className="db-sc" style={{ maxHeight: 300, overflowY: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>{["Name", "Email", "Phone", "Message", "Source"].map((h) => <th key={h} style={thSt}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {loading
                      ? Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>{[130, 160, 90, 200, 60].map((w, j) => <td key={j} style={{ padding: "10px 12px" }}><Shimmer h={12} w={w} /></td>)}</tr>
                      ))
                      : enquiries.map((e, i) => (
                        <tr key={i} className="db-tr">
                          <td style={{ ...tdSt, color: "#111827", fontWeight: 500, whiteSpace: "nowrap" }}>{e.name}</td>
                          <td style={{ ...tdSt, color: "#6b7280", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{e.email}</td>
                          <td style={{ ...tdSt, color: "#6b7280", whiteSpace: "nowrap" }}>{e.phone}</td>
                          <td style={{ ...tdSt, color: "#9ca3af", maxWidth: 240, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            <Tooltip title={e.message} placement="topLeft" styles={{ body: { maxWidth: 360 } }}>
                              <span style={{ cursor: "default" }}>{e.message}</span>
                            </Tooltip>
                          </td>
                          <td style={tdSt}><span style={{ display: "inline-flex", padding: "2px 7px", borderRadius: 20, fontSize: 11, fontWeight: 500, background: "rgba(99,102,241,0.1)", color: "#6366f1" }}>{e.utm_source || "—"}</span></td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
}
